import { useCallback, useMemo, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';
import { AlertTriangle, History, Loader2, Search } from 'lucide-react';
import { Badge, DataTable, Pagination, SearchInput, StatusMessage } from '@aimsos/ui';

import { useWorkspaceAuditLog } from '../hooks';
import { auditOutcomeBadgeVariant, formatAuditLogTimestamp } from '../utils';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

/**
 * Activity log tab — POST /v1/audit/list (pagination, `search.keyword`, sort by created_at DESC).
 * Table body scrolls; pagination is fixed below (same pattern as Users / Source Drives lists).
 * Route: /workspaces/:workspaceId/activity-log
 */
const WorkspaceActivityLogPage = () => {
  const { workspaceId } = useParams();
  const { t, i18n } = useTranslation('workspaces');
  const { loading: contextLoading } = useOutletContext();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [messageScrollHover, setMessageScrollHover] = useState(false);
  const [tableScrollHover, setTableScrollHover] = useState(false);

  const debouncedSearchKeyword = useDebounce(search, 350);

  const { events, meta, listLoading, listError } = useWorkspaceAuditLog({
    workspaceId,
    contextLoading,
    page,
    setPage,
    pageSize,
    debouncedSearchKeyword,
  });

  const rangeLabel = useMemo(() => {
    const total = meta.total_items;
    if (total === 0 || events.length === 0) return '0-0';
    const start = (meta.current_page - 1) * meta.per_page + 1;
    const end = start + events.length - 1;
    return `${start}-${end}`;
  }, [meta.current_page, meta.per_page, meta.total_items, events.length]);

  const handleSearchChange = useCallback((e) => setSearch(e.target.value), []);
  const handleSearchClear = useCallback(() => setSearch(''), []);

  const setPageSizeAndReset = useCallback((next) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const handlePrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'userFullName',
        label: t('detail.activityLog.columns.userName'),
        cellClassName: 'max-w-[160px]',
        render: (_, ev) => (
          <span className="truncate text-sm font-medium text-content">{ev.userFullName}</span>
        ),
      },
      {
        key: 'action',
        label: t('detail.activityLog.columns.action'),
        cellClassName: 'whitespace-nowrap text-content',
        render: (_, ev) => ev.action,
      },
      {
        key: 'section',
        label: t('detail.activityLog.columns.section'),
        cellClassName: 'text-sm font-medium uppercase tracking-wide text-content-secondary',
        render: (_, ev) => (ev.section ? ev.section.toUpperCase() : '—'),
      },
      {
        key: 'item',
        label: t('detail.activityLog.columns.item'),
        cellClassName: 'max-w-[200px] text-content-secondary',
        render: (_, ev) => (
          <span className="line-clamp-2 break-words text-sm">{ev.item}</span>
        ),
      },
      {
        key: 'description',
        label: t('detail.activityLog.columns.detail'),
        cellClassName: 'max-w-[280px] text-content-muted',
        render: (_, ev) => (
          <span className="line-clamp-2 break-words text-sm">{ev.description || '—'}</span>
        ),
      },
      {
        key: 'outcome',
        label: t('detail.activityLog.columns.outcome'),
        cellClassName: 'whitespace-nowrap',
        render: (_, ev) =>
          ev.outcome ? (
            <Badge
              variant={auditOutcomeBadgeVariant(ev.outcome)}
              size="small"
              className="!rounded-full"
            >
              {ev.outcome}
            </Badge>
          ) : (
            <span className="text-sm text-content-muted">—</span>
          ),
      },
      {
        key: 'createdAt',
        label: t('detail.activityLog.columns.timestamp'),
        cellClassName: 'whitespace-nowrap text-content-muted',
        render: (_, ev) => formatAuditLogTimestamp(ev.createdAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  if (contextLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col py-4">
        <StatusMessage
          icon={Loader2}
          iconClassName="animate-spin"
          title={t('common.loading')}
          subtitle={t('detail.tabLoadingSubtitle')}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 py-4">
      <div className="flex shrink-0 flex-wrap items-center gap-4">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          placeholder={t('detail.activityLog.searchPlaceholder')}
          className="max-w-sm"
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {listError && events.length === 0 && !listLoading ? (
          <div
            className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto"
            data-scrollbar-hover={messageScrollHover ? 'true' : undefined}
            onMouseEnter={() => setMessageScrollHover(true)}
            onMouseLeave={() => setMessageScrollHover(false)}
          >
            <StatusMessage
              icon={AlertTriangle}
              title={t('detail.activityLog.listError')}
              subtitle={listError}
            />
          </div>
        ) : !listLoading && events.length === 0 ? (
          <div
            className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto"
            data-scrollbar-hover={messageScrollHover ? 'true' : undefined}
            onMouseEnter={() => setMessageScrollHover(true)}
            onMouseLeave={() => setMessageScrollHover(false)}
          >
            {debouncedSearchKeyword.trim() ? (
              <StatusMessage
                icon={Search}
                title={t('detail.activityLog.noSearchResults')}
                subtitle={t('detail.activityLog.noSearchResultsHint')}
              />
            ) : (
              <StatusMessage
                icon={History}
                title={t('detail.activityLog.empty.title')}
                subtitle={t('detail.activityLog.empty.description')}
              />
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-edge bg-surface">
              <div
                className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-x-auto overflow-y-auto"
                data-scrollbar-hover={tableScrollHover ? 'true' : undefined}
                onMouseEnter={() => setTableScrollHover(true)}
                onMouseLeave={() => setTableScrollHover(false)}
              >
                <DataTable
                  columns={columns}
                  data={events}
                  loading={listLoading}
                  minTableWidth={1040}
                />
              </div>
            </div>

            {meta.total_items > 0 && (
              <Pagination
                rangeLabel={rangeLabel}
                totalItems={meta.total_items}
                currentPage={meta.current_page}
                totalPages={meta.total_pages}
                hasPrevious={meta.has_previous}
                hasNext={meta.has_next}
                perPage={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                onPageSizeChange={setPageSizeAndReset}
                disabled={listLoading}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceActivityLogPage;
